import { useState, useEffect, useRef } from 'react'
import { searchActors } from '../api/tmdb'
import { ACTOR_POOL } from '../utils/dailyPuzzleGen'
import ActorPickerView from './ActorPickerView'
import BottomNav from './BottomNav'
import { IconArrowLeft, IconHome } from './icons'

function shuffleArray(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

function getDifficulty(a, b) {
  const minPop = Math.min(a.popularity || 0, b.popularity || 0)
  const avgPop = ((a.popularity || 0) + (b.popularity || 0)) / 2
  if (minPop >= 10) return { label: 'Easy' }
  if (avgPop >= 5) return { label: 'Medium' }
  return { label: 'Hard' }
}

export default function SetupScreen({ onStart, onBack, onHome, initialActors, onActorsChange }) {
  const [actors, setActors] = useState(() => initialActors || [null, null])

  const setActorsAndNotify = (next) => {
    setActors(next)
    onActorsChange?.(next)
  }
  const [activeSlot, setActiveSlot] = useState(0)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [loadingRandom, setLoadingRandom] = useState(false)
  const [genderFilter, setGenderFilter] = useState('any')
  const queueRef = useRef({ filter: 'any', queue: [] })
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [activeSlot])

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await searchActors(query.trim())
        setResults(data.filter((p) => p.profile_path).slice(0, 12))
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [query])

  const handlePickActor = (actor) => {
    const next = [...actors]
    next[activeSlot] = actor
    setActorsAndNotify(next)
    setQuery('')
    setResults([])
    if (activeSlot === 0 && !next[1]) setActiveSlot(1)
    else if (activeSlot === 1 && !next[0]) setActiveSlot(0)
  }

  const clearSlot = (i) => {
    const next = [...actors]
    next[i] = null
    setActorsAndNotify(next)
    setActiveSlot(i)
  }

  const handleRandomActor = async () => {
    setLoadingRandom(true)
    try {
      const current = queueRef.current
      let queue = current.queue
      if (current.filter !== genderFilter || queue.length === 0) {
        const pool = genderFilter === 'any' ? ACTOR_POOL : ACTOR_POOL.filter(a => a.gender === genderFilter)
        queue = shuffleArray(pool)
      }
      const entry = queue[queue.length - 1]
      queueRef.current = { filter: genderFilter, queue: queue.slice(0, -1) }
      if (!entry) return
      const data = await searchActors(entry.name)
      const actor = data.find(r => r.name.toLowerCase() === entry.name.toLowerCase()) || data[0]
      if (actor) handlePickActor(actor)
    } catch {
      // ignore
    } finally {
      setLoadingRandom(false)
    }
  }

  const sameActor = actors[0] && actors[1] && actors[0].id === actors[1].id
  const canStart = actors[0] && actors[1] && !sameActor
  const difficulty = canStart ? getDifficulty(actors[0], actors[1]) : null
  const submitHelper = sameActor
    ? 'Choose two different actors!'
    : 'Pick a start and goal actor to begin'

  return (
    <div className="setup">
      <div className="screen-content">
        <ActorPickerView
          slots={[
            {
              role: 'START',
              label: 'Choose start actor',
              actor: actors[0],
              state: activeSlot === 0 ? 'active' : 'inactive',
              onSelect: () => setActiveSlot(0),
              onClear: actors[0] ? () => clearSlot(0) : null,
            },
            {
              role: 'GOAL',
              label: 'Choose end actor',
              actor: actors[1],
              state: activeSlot === 1 ? 'active' : 'inactive',
              onSelect: () => setActiveSlot(1),
              onClear: actors[1] ? () => clearSlot(1) : null,
            },
          ]}
          selectingLabel={activeSlot === 0 ? 'Start Actor' : 'End Actor'}
          genderFilter={genderFilter}
          onGenderChange={setGenderFilter}
          query={query}
          onQueryChange={setQuery}
          inputRef={inputRef}
          searching={searching}
          results={results}
          onResultPick={handlePickActor}
          noResults={!searching && query.trim().length >= 2 && results.length === 0}
          rolling={loadingRandom}
          onRoll={handleRandomActor}
          submitLabel="Start Game"
          canSubmit={!!canStart}
          onSubmit={() => canStart && onStart(actors[0], actors[1])}
          submitHelper={submitHelper}
          extra={difficulty && (
            <div className="picker__difficulty">
              <span className={`difficulty-pill difficulty-pill--${difficulty.label.toLowerCase()}`}>
                <span className="difficulty-pill__dot" aria-hidden="true" />
                {difficulty.label}
              </span>
              <span className="picker__difficulty-hint">estimated difficulty</span>
            </div>
          )}
        />

        <BottomNav items={[
          { key: 'parent', icon: IconArrowLeft, label: 'Single Player', variant: 'inverse', onClick: onBack },
          { key: 'home',   icon: IconHome,      label: 'Home',          variant: 'primary', onClick: onHome },
        ]} />
      </div>
    </div>
  )
}
